import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AsyncSelect from "react-select/async";
import MaterialTable from "material-table";
import { getTrans as t } from "../../Localization/translations";
import { API } from "../../backend";
import authHeader from "../../services/auth-header";
import Helper from "../../Helper";
import CreditMemoService from "../../services/CreditMemo/creditMemoService";
import lineOfBusinessService from "../../services/company/lineofbusinessService";
import subcompanyService from "../../services/company/subcompanyService";
import axios from "axios";

const REASON_OPTIONS = [
  { value: "Billing Error", label: "Billing Error" },
  { value: "Duplicate Invoice", label: "Duplicate Invoice" },
  { value: "Overpayment", label: "Overpayment" },
  { value: "Service Not Rendered", label: "Service Not Rendered" },
  { value: "Discount Applied", label: "Discount Applied" },
  { value: "Other", label: "Other" },
];

export default function EditCreditMemo() {
  const location = useLocation();
  const navigate = useNavigate();
  const creditMemoId = location.state;

  const [creditMemoDate, setCreditMemoDate] = useState(new Date());
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [reference, setReference] = useState("");
  const [cmStatus, setCmStatus] = useState("");
  const [cmNumber, setCmNumber] = useState("");
  const [appliedAmount, setAppliedAmount] = useState(0);
  const [companyName, setCompanyName] = useState("");
  const [lobList, setLobList] = useState([]);
  const [lineOfBusiness, setLineOfBusiness] = useState("");
  const [subcompanyList, setSubcompanyList] = useState([]);
  const [subcompany, setSubcompany] = useState("");
  const [dueInvoices, setDueInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [cm, setCm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyAmounts, setApplyAmounts] = useState({});
  const [applyLoading, setApplyLoading] = useState(null);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 1;

  // ── Load existing data ─────────────────────────────────────────
  useEffect(() => {
    if (!creditMemoId) { navigate("/creditmemo"); return; }

    // Load LOBs
    lineOfBusinessService.getLineOfBusiness()
      .then((res) => setLobList(res.data?.data || res.data || []))
      .catch(() => { });

    // Load SubCompanies
    subcompanyService.getSubcompany()
      .then((res) => setSubcompanyList(res.data?.data || res.data || []))
      .catch(() => { });


    CreditMemoService.getById(creditMemoId).then((res) => {
      const data = res.data;
      setCreditMemoDate(new Date(data.credit_memo_date));
      setAmount(data.amount || "");
      setReason(data.reason || "");
      setDescription(data.description || "");
      setReference(data.reference || "");
      setCmStatus(data.status || "");
      setCmNumber(data.credit_memo_number || "");
      setAppliedAmount(data.applied_amount || 0);
      setAttachments(data.attachments || []);
      setLineOfBusiness(data.line_of_business || "");
      setSubcompany(data.subcompany || "");

      if (data.customer && data.customer_name) {
        const cust = { id: data.customer, customer_name: data.customer_name };
        setSelectedCustomer(cust);
        loadDueInvoices(cust);
      }
    });
  }, [creditMemoId, navigate]);

  // ── Load credit memo details (used after apply) ─────────────────
  const loadCM = () => {
    CreditMemoService.getById(creditMemoId).then((res) => {
      const data = res.data;
      setCm(data);
      setAttachments(data.attachments || []);
      setLoading(false);

      if (data.customer_name) {
        axios.get(
          API + `billing/invoiceswithdue/?per_page=100&page=1&search=&contractNumber=&customerName=${data.customer_name}&contractid=undefined`,
          { headers: authHeader() }
        ).then((r) => {
          setDueInvoices(r.data.data || r.data.results || []);
        }).catch(() => setDueInvoices([]));
      }
    }).catch(() => {
      toast.error("Failed to load credit memo");
      setLoading(false);
    });
  };

  // ── Load due invoices ──────────────────────────────────────────
  const loadDueInvoices = async (customer) => {
    if (!customer) { setDueInvoices([]); return; }
    setInvoicesLoading(true);
    try {
      const res = await axios.get(
        API + `billing/invoiceswithdue/?per_page=100&page=1&search=&contractNumber=&customerName=${customer.customer_name}&contractid=undefined`,
        { headers: authHeader() }
      );
      setDueInvoices(res.data.data || res.data.results || []);
    } catch (e) {
      setDueInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  // ── Check if approval is required for Credit Memo ─────────────────
  const [isApprovalRequired, setIsApprovalRequired] = useState(false);

  useEffect(() => {
    axios.get(API + "accounting/transactiontypesview/?transaction_type=Credit Memo", { headers: authHeader() })
      .then((res) => {
        const list = res.data.data || [];
        if (list.length > 0) {
          const approvalFlag = (list[0].approval || "").trim().toUpperCase();
          setIsApprovalRequired(approvalFlag === "Y");
        }
      })
      .catch(() => { });
  }, []);

  // ── Apply to invoice ──────────────────────────────────────────
  const handleApplyToInvoice = async (invoice) => {
    const applyAmt = parseFloat(applyAmounts[invoice.id] || 0);
    const unapplied = parseFloat(((cm.amount || 0) - (cm.applied_amount || 0)).toFixed(2));

    if (!applyAmt || applyAmt <= 0) { toast.error("Enter a valid apply amount"); return; }
    if (applyAmt > (invoice.balance_amount || 0)) { toast.error("Apply amount cannot exceed invoice remaining amount"); return; }
    if (applyAmt > unapplied) { toast.error(`Apply amount cannot exceed unapplied amount (${Helper.Amount(unapplied)})`); return; }

    setApplyLoading(invoice.id);
    try {
      await CreditMemoService.applyToInvoice(creditMemoId, invoice.id, applyAmt);
      toast.success("Credit memo applied to invoice successfully", { autoClose: 1000 });
      setApplyAmounts((prev) => ({ ...prev, [invoice.id]: "" }));
      loadCM();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Apply failed");
    } finally {
      setApplyLoading(null);
    }
  };

  // ── Customer loader ────────────────────────────────────────────
  const loadCustomerOptions = async (inputValue) => {
    try {
      const res = await axios.get(
        API + `parties/vendor/?party_type=Customer&&customer_name=${inputValue}`,
        { headers: authHeader() }
      );
      return res.data.data || [];
    } catch { return []; }
  };

  const handleCustomerChange = (value) => {
    setSelectedCustomer(value);
    loadDueInvoices(value);
  };

  // ── Submit ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomer) { toast.error("Please select a customer"); return; }
    if (!amount || parseFloat(amount) <= 0) { toast.error("Amount must be greater than 0"); return; }

    try {
      await CreditMemoService.update(creditMemoId, {
        credit_memo_date: Helper.DatePicker(creditMemoDate),
        customer: selectedCustomer.id,
        amount: parseFloat(amount),
        reason,
        description,
        reference,
        ...(lineOfBusiness ? { line_of_business: lineOfBusiness } : {}),
        ...(subcompany ? { subcompany: subcompany } : {}),
      });
      toast.success("Credit Memo updated successfully", { autoClose: 1000 });
      setTimeout(() => navigate("/creditmemo/view", { state: creditMemoId }), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update credit memo");
    }
  };

  // ── Document pagination ────────────────────────────────────────
  const totalPages = Math.ceil(attachments.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentDocuments = attachments.slice(indexOfFirstRecord, indexOfLastRecord);

  const unapplied = parseFloat(((parseFloat(amount) || 0) - (appliedAmount || 0)).toFixed(2));

  return (
    <div className="page-wrapper">
      <ToastContainer />
      <div className="content container-fluid">

        {/* ── Header ── */}
        <div className="header pb-0 pt-3">
          <div className="d-lg-flex">
            <div>
              <h5 className="mb-3">{t("Edit Credit Memo")}</h5>
              <h6>
                <Link to="/creditmemo" className="tc">{t("Credit Memos")}</Link>
                {" "} / <span>{t("Edit")}</span>
              </h6>
            </div>
          </div>
        </div>

        {/* ── Two column row ── */}
        <div className="row mb-2">
          <div className="col-md-12">
            <div className="row">

              {/* ── LEFT col-md-8 — Form Card ── */}
              <div className="col-md-8">
                <div className="card">
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>

                      {/* Row 0: Sub Company | Line of Business */}
                      <div className="form-group row">

                        <div className="col-xl-6">
                          <label className="col-lg-12 col-form-label">{t("Sub Company")}</label>
                          <div className="col-lg-12">
                            <select
                              className="form-control"
                              value={subcompany}
                              onChange={(e) => setSubcompany(e.target.value)}
                            >
                              <option value="">{t("Select Sub Company")}</option>
                              {subcompanyList.map((sc) => (
                                <option key={sc.id} value={sc.id}>
                                  {sc.sub_company_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="col-xl-6">
                          <label className="col-lg-12 col-form-label">{t("Line of Business")}</label>
                          <div className="col-lg-12">
                            <select
                              className="form-control"
                              value={lineOfBusiness}
                              onChange={(e) => setLineOfBusiness(e.target.value)}
                            >
                              <option value="">{t("Select Line of Business")}</option>
                              {lobList.map((lob) => (
                                <option key={lob.id} value={lob.id}>
                                  {lob.lob_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>



                      {/* Row 1: Customer | Date */}
                      <div className="form-group row">
                        <div className="col-xl-6">
                          <label className="col-lg-12 col-form-label required">{t("Customer")}</label>
                          <div className="col-lg-12">
                            <AsyncSelect
                              cacheOptions
                              defaultOptions
                              isClearable
                              placeholder={t("Search Customer")}
                              value={selectedCustomer}
                              loadOptions={loadCustomerOptions}
                              onChange={handleCustomerChange}
                              getOptionLabel={(e) => e.customer_name}
                              getOptionValue={(e) => e.id}
                            />
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <label className="col-lg-12 col-form-label required">{t("Date")}</label>
                          <div className="col-lg-12">
                            <DatePicker
                              className="form-control datepick"
                              selected={creditMemoDate}
                              onChange={(d) => setCreditMemoDate(d)}
                              dateFormat="MM/dd/yyyy"
                              showYearDropdown
                              scrollableYearDropdown
                              showMonthDropdown
                              dropdownMode="select"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Reason | Reference */}
                      <div className="form-group row">
                        <div className="col-xl-6">
                          <label className="col-lg-12 col-form-label">{t("Reason")}</label>
                          <div className="col-lg-12">
                            <select
                              className="form-control"
                              value={reason}
                              onChange={(e) => setReason(e.target.value)}
                            >
                              <option value="">{t("Select Reason")}</option>
                              {REASON_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-xl-6">
                          <label className="col-lg-12 col-form-label">{t("Reference")}</label>
                          <div className="col-lg-12">
                            <input
                              className="form-control"
                              value={reference}
                              onChange={(e) => setReference(e.target.value)}
                              placeholder={t("Reference")}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Row 3: Description */}
                      <div className="col-xl-6">
                        <label className="col-lg-12 col-form-label">{t("Description")}</label>
                        <div className="col-lg-12">
                          <textarea
                            className="form-control"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t("Description")}
                          />
                        </div>
                      </div>

                      {/* ── Buttons ── */}
                      <div className="text-left py-3 d-flex flex-wrap gap-2 align-items-center">
                        <button
                          type="button"
                          className="btn-cancel-invoice border-0 btn btn-secondary btn-gradient-primary"
                          onClick={() => navigate("/creditmemo", { state: creditMemoId })}
                        >
                          {t("Cancel")}
                        </button>
                        &nbsp;
                        <button
                          type="submit"
                          className="btn-continue border-0 btn btn-primary btn-gradient-primary btn"
                        >
                          {t("Update")}
                        </button>
                        &nbsp;&nbsp;
                        {isApprovalRequired && (
                          <div style={{ marginLeft: "15px", fontSize: "13px", color: "#6c757d" }}>
                            This transaction needs to be submitted for approval.
                            <br />
                            Please navigate to the view screen to submit for approval.
                          </div>
                        )}
                      </div>

                    </form>
                  </div>
                </div>
              </div>

              {/* ── RIGHT col-md-4 ── */}
              <div className="col-md-4">

                {/* Amounts Card */}
                <div className="card">
                  <div className="card-body">

                    <div className="col-xl-12">
                      <div className="form-group row">
                        <label className="col-lg-12 col-form-label">{t("CM Number")}</label>
                        <div className="col-lg-12">
                          <input className="form-control" value={cmNumber} disabled />
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-12">
                      <div className="form-group row">
                        <label className="col-lg-12 col-form-label required">{t("Credit Amount")}</label>
                        <div className="col-lg-12">
                          <input
                            className="form-control"
                            type="number"
                            min="0"
                            step="0.01"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-12">
                      <div className="form-group row">
                        <label className="col-lg-12 col-form-label">{t("Applied Amount")}</label>
                        <div className="col-lg-12">
                          <input className="form-control" value={Helper.Amount(appliedAmount)} disabled />
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-12">
                      <div className="form-group row">
                        <label className="col-lg-12 col-form-label">{t("Unapplied Amount")}</label>
                        <div className="col-lg-12">
                          <input className="form-control" value={Helper.Amount(unapplied)} disabled />
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Documents Card — separate card like receipt */}
                <div className="card">
                  <div className="card-body">

                    <h6 className="mb-3">{t("Documents")} ({attachments.length})</h6>

                    <div className="documents-list">
                      {attachments.length === 0 ? (
                        <p className="text-muted text-center small">No documents</p>
                      ) : (
                        <>
                          {currentDocuments.map((doc) => (
                            <div className="document-item" key={doc.id}>
                              <div className="document-icon">📄</div>
                              <div className="document-info">
                                <div className="document-name" style={{ fontSize: 12 }}>
                                  {doc.file?.split("/").pop() || "File"}
                                </div>
                                <div className="document-metadata text-muted" style={{ fontSize: 11 }}>
                                  {doc.uploaded_at ? Helper.DateNormal(doc.uploaded_at) : ""}
                                </div>
                              </div>
                              <div className="document-actions">
                                <button
                                  className="btn-continue border-0 btn btn-primary btn-gradient-primary btn-sm"
                                  onClick={() => window.open(doc.file_url || doc.file, "_blank")}
                                >
                                  Download
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Pagination — same as receipt */}
                          <div className="pagination-controls d-flex justify-content-between mt-3">
                            <button
                              className="pag-btn"
                              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button
                              className="pag-btn"
                              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                  </div>
                </div>

              </div>
              {/* end col-md-4 */}

            </div>
          </div>
        </div>
        {/* end row mb-2 */}

        {/* ── Open Invoices Table (full width, below) ── */}
        {/* <div className="pt-3 mt-2">
          <h5 className="mb-3">{t("Open Invoices")}</h5>
          <MaterialTable
            style={{
              backgroundColor: "var(--bs-table-bg)",
              borderBottomWidth: "1px",
              boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
            }}
            columns={[
              {
                title: t("Invoice Number"),
                field: "invoice_number",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (row) => (
                  <div className="hyperlink" onClick={() => navigate("/billing/viewinvoices", { state: row.id })}>
                    {row.invoice_number}
                  </div>
                ),
              },
              {
                title: t("Transaction Date"),
                field: "creation_date",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (row) => Helper.DateNormal(row.creation_date),
              },
              {
                title: t("Due Date"),
                field: "due_date",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (row) => row.due_date ? Helper.DateNormal(row.due_date) : "-",
              },
              {
                title: t("Invoice Amount"),
                field: "total_amount",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "right" },
                cellStyle: { textAlign: "right", fontSize: 12 },
                render: (row) => <div className="d-none d-xl-block text-right" style={{ textAlign: "right", fontSize: 12 }}>{Helper.Amount(row.total_amount)}</div>,
              },
              {
                title: t("Remaining Amount"),
                field: "balance_amount",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "right" },
                cellStyle: { textAlign: "right", fontSize: 12 },
                render: (row) => <div className="d-none d-xl-block text-right" style={{ textAlign: "right", fontSize: 12 }}>{Helper.Amount(row.balance_amount)}</div>,
              },
              {
                title: t("Day Past Due"),
                field: "due_date",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (row) => Helper.DueDays(row.due_date),
              },
              {
                title: t("Contract No"),
                field: "contract_id",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (row) => row.contract_id?.contract_number || "-",
              },
              {
                title: t("Customer Name"),
                field: "customer_name",
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                cellStyle: { textAlign: "left", fontSize: 12 },
                render: (row) => row.customer_name || cm.customer_name || "-",
              },
              {
                title: t("Apply Amount"),
                headerStyle: { verticalAlign: "inherit", fontWeight: "bold", textAlign: "left" },
                render: (row) => (
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: "100px" }}
                    min="0"
                    step="0.01"
                    max={Math.min(row.balance_amount || 0, unapplied)}
                    value={applyAmounts[row.id] || ""}
                    onChange={(e) => setApplyAmounts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                    placeholder="0.00"
                  />
                ),
              },
              {
                title: "",
                render: (row) => (
                  <button
                    className="btn-apply border-0 btn btn-primary btn-gradient-primary btn-sm"
                    onClick={() => handleApplyToInvoice(row)}
                    disabled={applyLoading === row.id}
                  >
                    {applyLoading === row.id ? "..." : t("APPLY")}
                  </button>
                ),
              },
            ]}
            data={dueInvoices}
            options={{
              headerStyle: { backgroundColor: "white", color: "#245C7F", fontSize: 11 },
              rowStyle: (_, i) => i % 2 ? { backgroundColor: "#fff" } : {},
              sorting: true,
              search: false,
              toolbar: false,
              paging: true,
              pageSize: 10,
              emptyRowsWhenPaging: false,
              pageSizeOptions: [10, 20, 25, 50],
              paginationType: "stepped",
              showFirstLastPageButtons: false,
              actionsColumnIndex: -1,
            }}
          />
        </div> */}


      </div>
    </div>
  );
}
